// Libraries
import { combine, err, ok, Result } from "neverthrow";
// Errors
import KtznEventSubscriptionError from "@/framework/errors/websocket/ktznEventSubscriptionError";
// Types
import { WsMessage, WsStatus } from "@/framework/network/websocket/websocket";
import { NotifyMyEvent } from "@/framework/services/events/subscription";
// Services
import KtznWebsocketService, {
  IKtznWebsocketService,
  KtznSubscribeEvent,
  SendEventType,
} from "@/framework/network/websocket/ktznWebsocketService";
import { INotifyMyEventService, NotifyMyEventService } from "@/framework/services/events/notifyMyEventService";
import ErrorLogService, { IErrorLogService } from "@/framework/logger/errorLog/errorLogService";
// Repositories
import {
  IKtznWebsocketSubscriptionCache,
  KtznEventSubscription,
  KtznEventSubscriptionState,
  KtznWebsocketSubscriptionCache,
} from "@/framework/repositories/ktznEventSubscriptionCache";
import AuthenticationRepository, { IAuthenticationRepository } from "@/framework/repositories/authenticationRepository";
import { GeneralResponse } from "@/framework/objects/GeneralResponse";
import KtznWebsocketError from "@/framework/errors/websocket/ktznWebsocketError";
import { IProjectService, KtznProject, ProjectService } from "@/framework/services/general/projectService";
import store from "@/store";

export interface IKtznEventSubscriptionRepository {
  websocketConnectedHandler(wsEvent: any): Promise<Result<KtznEventSubscription[], KtznEventSubscriptionError>>;

  websocketSystemMessageHandler(systemMessage: WsMessage | undefined): void;

  websocketMessageHandler(ktznEvent: NotifyMyEvent | undefined): void;

  websocketFailureHandler(wsEvent: any): void;

  websocketDisconnectedHandler(wsEvent: any): void;

  connectToSubscribedEvents(): Promise<Result<KtznEventSubscription[], KtznEventSubscriptionError>>;

  endSubscription(eventDataList: any): Promise<Result<GeneralResponse[], KtznWebsocketError>>; // needed?

  subscribeToEvent(subscribeName: string): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>>;

  unsubscribeFromEvent(subscribeName: string): Promise<Result<null, KtznEventSubscriptionError>>;

  unsubscribeFromAllEvents(): Promise<Result<null[], KtznEventSubscriptionError>>;

  subscribeToUser(accessId: string | undefined): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>>;

  subscribeToTenant(tenantId: number | undefined): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>>;

  subscribeToConversation(
    conversationId: string | undefined
  ): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>>;

  subscribeToStaffGroup(staffGroupId: number): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>>;

  subscribeToConversations(
    conversationIds: string[] | undefined
  ): Promise<Result<KtznEventSubscription[], KtznEventSubscriptionError>>;

  unsubscribeConversationEvents(): Promise<Result<null, KtznEventSubscriptionError>>; // needed?

  reSubscribe(): Promise<Result<KtznEventSubscription[], KtznEventSubscriptionError>>;

  start(userAccessId: string, webSocketUri: string): Promise<Result<null, KtznEventSubscriptionError>>;

  stop(): Promise<Result<null, KtznEventSubscriptionError>>;
}

/**
 * Handles the Kotozna event subscription logic.
 *
 * For example:
 * - Start and stop of the websocket connection
 * - Handle the incoming events
 * - Subscribe, unsubscribe, re-subscribe
 */
export class KtznEventSubscriptionRepository implements IKtznEventSubscriptionRepository {
  userAccessId: string | undefined;
  tenantId: string | undefined;

  private readonly project: KtznProject;
  private _authenticationRepository: IAuthenticationRepository;
  private readonly _ktznWebsocketService: IKtznWebsocketService;
  private _subscriptionCache: IKtznWebsocketSubscriptionCache;
  private _errorLogService: IErrorLogService;
  private _notifyMyEventService: INotifyMyEventService;
  private _projectService: IProjectService;
  private _connectionStarted = false;

  public constructor(
    authenticationRepository: IAuthenticationRepository = AuthenticationRepository,
    ktznWebsocketService: IKtznWebsocketService = KtznWebsocketService,
    ktznWebsocketSubscriptionCache: IKtznWebsocketSubscriptionCache = new KtznWebsocketSubscriptionCache(),
    errorLogService: IErrorLogService = ErrorLogService,
    notifyMyEventService: INotifyMyEventService = new NotifyMyEventService(),
    projectService: IProjectService = new ProjectService(),
    registerEventHandlers: boolean = true
  ) {
    this._authenticationRepository = authenticationRepository;
    this._ktznWebsocketService = ktznWebsocketService;
    this._subscriptionCache = ktznWebsocketSubscriptionCache;
    this._errorLogService = errorLogService;
    this._notifyMyEventService = notifyMyEventService;
    this._projectService = projectService;

    // Register event handlers for conversation repository
    if (registerEventHandlers && this._ktznWebsocketService) {
      this._ktznWebsocketService.connected.on(this.websocketConnectedHandler);
      this._ktznWebsocketService.systemMessage.on(this.websocketSystemMessageHandler);
      this._ktznWebsocketService.message.on(this.websocketMessageHandler);
      this._ktznWebsocketService.failure.on(this.websocketFailureHandler);
      this._ktznWebsocketService.disconnected.on(this.websocketDisconnectedHandler);
    }

    // Get the current project
    const project = this._projectService.getCurrent();
    if (project) {
      this.project = project;
    } else {
      throw new Error("Error in KtznEventSubscriptionRepository constructor: KtznProject not found.");
    }
  }

  // MARK: - Websocket event listeners

  /**
   * Handles the websocket connected events and connects the the subscribed events.
   * @param wsEvent Websocket event
   * @returns Result that can be either an success (`ok` with a list of `KtznEventSubscription` values) or an `KtznEventSubscriptionError`
   */
  websocketConnectedHandler = async (
    wsEvent: any
  ): Promise<Result<KtznEventSubscription[], KtznEventSubscriptionError>> => {
    return await this.connectToSubscribedEvents();
  };

  /**
   * Handles the websocket SystemMessage events, sent by the websocket server and acts by re-subscribing, unsubscribing or updating a subscription state.
   * @param systemMessage WsMessage sent by the websocket server (e.g. `CONNECTED`, `DISCONNECT`, `SUBSCRIBED`, `UNSUBSCRIBED`)
   */
  websocketSystemMessageHandler = async (systemMessage: WsMessage | undefined): Promise<void> => {
    if (!systemMessage) return;
    switch (systemMessage.status) {
      case WsStatus.CONNECTED:
        console.log("WsStatus.CONNECTED: ", systemMessage);
        await this.reSubscribe();
        break;
      case WsStatus.DISCONNECT:
        console.log("WsStatus.DISCONNECT: ", systemMessage);
        this._subscriptionCache.unsubscribeAll();
        console.log("list unsubscribed: ", this._subscriptionCache.listUnsubscribed());
        if (systemMessage.reason == "invalid_token") {
          await this._authenticationRepository.authRefresh();
        }
        break;
      case WsStatus.REDIS_CONNECTION_DISCONNECT:
        console.log("WsStatus.REDIS_CONNECTION_DISCONNECT: ", systemMessage);
        break;
      case WsStatus.REJECT:
        console.log("WsStatus.REJECT: ", systemMessage);
        break;
      case WsStatus.SUBSCRIBED:
        console.log("WsStatus.SUBSCRIBED: ", systemMessage);
        if (systemMessage.subscribeName && systemMessage.status) {
          this._subscriptionCache.updateState(systemMessage.subscribeName, KtznEventSubscriptionState.Connected);
        }
        break;
      case WsStatus.UNSUBSCRIBED:
        console.log("WsStatus.UNSUBSCRIBED: ", systemMessage);
        if (systemMessage.subscribeName && systemMessage.status) {
          this._subscriptionCache.updateState(systemMessage.subscribeName, KtznEventSubscriptionState.Disconnected);
        }
        break;
      default:
        break;
    }
  };

  /**
   * Handles the incoming Kotozna events (NotifyMyEvent) and forwards it to the parser.
   * @param ktznEvent Events (NotifyMyEvent) that contain the Kotozna business logic sent from `ktzn-be`. (e.g. a chat message or an conversation status update...)
   */
  websocketMessageHandler = (ktznEvent: NotifyMyEvent | undefined): void => {
    if (!ktznEvent) return;
    this._notifyMyEventService.parse(ktznEvent);
  };

  /**
   * Handles websocket failure events. (Does currently nothing)
   * @param wsEvent A general websocket event
   */
  websocketFailureHandler = (wsEvent: any): void => {};

  /**
   * Handles websocket disconnected events by re-setting the connection status value and unsubscribing from all events.
   * @param wsEvent A general websocket event
   */
  websocketDisconnectedHandler = (wsEvent: any): void => {
    console.log("websocketDisconnectedHandler called");
    this._connectionStarted = false;
    this._subscriptionCache.unsubscribeAll();

    console.log("websocketDisconnectedHandler - connectionStarted", this._connectionStarted);
    console.log("websocketDisconnectedHandler - list", this._subscriptionCache.list());

    switch (this.project) {
      case KtznProject.Guest:
        // Don't reconnect if redirected to the lock-screen
        if (window.location.pathname.indexOf("customer/locked") === -1)
          this._ktznWebsocketService.setAutoReconnect(true);
        else this._ktznWebsocketService.setAutoReconnect(false);
        break;
      case KtznProject.Pwa:
        // Use the isUserLoggedOut value to toggle auto-reconnect for WebSocket
        if (!store.getters.isUserLoggedOut) this._ktznWebsocketService.setAutoReconnect(true);
        else this._ktznWebsocketService.setAutoReconnect(false);
        break;
      case KtznProject.Staff:
        // Use the isUserLoggedOut value to toggle auto-reconnect for WebSocket
        if (!store.getters.isUserLoggedOut) this._ktznWebsocketService.setAutoReconnect(true);
        else this._ktznWebsocketService.setAutoReconnect(false);
        break;
      default:
        break;
    }
  };

  // MARK: - Subscriptions

  /**
   * Connects to the subscribed events from the cache.
   *
   * Is this still in use???
   * @returns Result that either contains a list successfully resubscribed KtznEventSubscriptions or an KtznEventSubscriptionError.
   */
  connectToSubscribedEvents = async (): Promise<Result<KtznEventSubscription[], KtznEventSubscriptionError>> => {
    let promises: Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>>[] = [];
    let list = this._subscriptionCache.list();
    list.forEach((item: KtznEventSubscription) => {
      if (item && item.event && item.event.subscribe_name) {
        promises.push(this.subscribeToEvent(item.event.subscribe_name));
      } else {
        console.log("connectToSubscribedEvents error: No subscribe_name for item: ", item);
      }
    });
    try {
      // Return the combine result
      return combine(await Promise.all(promises));
    } catch (error) {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.general, error));
    }
  };

  /**
   * Ends a subscription and triggers `sendEventReceived`.
   *
   * Is this still needed? (It was needed in the past to send the `sendEventReceived` to `ktnz-be`.) Currently the KtnzWebsocketService handles sending `sendEventReceived` for every incoming event.
   * @param eventDataList General event?
   * @returns Result that can be either an success (`ok` with a list of `GeneralResponse` values) or an `KtznEventSubscriptionError`
   */
  endSubscription = async (eventDataList: any): Promise<Result<GeneralResponse[], KtznWebsocketError>> => {
    let promises: Promise<Result<GeneralResponse, KtznWebsocketError>>[] = [];
    for (const e of eventDataList) {
      if (e && e.event_id) {
        console.log("endSubscription event : ", e);
        promises.push(this._ktznWebsocketService.sendEventReceived(e.event_id));
      } else {
        console.log("endSubscription error: No event_id");
      }
    }
    try {
      // Return the combine result
      return combine(await Promise.all(promises));
    } catch (error) {
      return err(new KtznWebsocketError(KtznWebsocketError.Type.general, error));
    }
  };

  /**
   * Subscribes to an event based on the subscribe name.
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @returns Result that can be either an success (`ok` with `KtznEventSubscription` value) or an `KtznEventSubscriptionError`
   */
  subscribeToEvent = async (
    subscribeName: string
  ): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>> => {
    if (!subscribeName || subscribeName == "") {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.noSubscribeName));
    }
    const state = this._subscriptionCache.stateByName(subscribeName);
    if (this._subscriptionCache.existByName(subscribeName) && state == KtznEventSubscriptionState.Connected) {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.subscriptionExists));
    }

    const result = await this._authenticationRepository.getTokenString();
    if (result && result.isOk()) {
      const event = this._ktznWebsocketService.send(SendEventType.Subscribe, subscribeName, result.value, "ktzn_auth");
      if (event && event.isOk()) {
        const res = this._subscriptionCache.addByEvent(event.value);
        if (res.isOk()) {
          return ok(res.value);
        } else {
          return err(
            new KtznEventSubscriptionError(
              KtznEventSubscriptionError.Type.cacheSubscriptionExists,
              event && event.isErr() ? event.error.message : ""
            )
          );
        }
      } else {
        // Add to cache even if not connected;
        const eventFallback: KtznSubscribeEvent = {
          event: SendEventType.Subscribe,
          subscribe_name: subscribeName,
          token: result.value,
          user_agent: navigator.userAgent,
          method: "ktzn_auth",
        };
        this._subscriptionCache.addByEvent(eventFallback);
        return err(
          new KtznEventSubscriptionError(
            KtznEventSubscriptionError.Type.sendFailed,
            event && event.isErr() ? event.error.message : ""
          )
        );
      }
    } else {
      const res = await this._errorLogService.sendAuthLog(
        "Auth.currentAuthenticatedUser",
        "getNewToken",
        {},
        result ? result.error.message : ""
      );
      if (res && res.isErr()) {
        console.log("sendAuthLog failed: ", res.error.message);
      }
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.getCogntioTokenFailed));
    }
  };

  /**
   * Unsubscribes from an event based on the subscribe name.
   * @param subscribeName Subscription name (e.g. 'conversations/<id>')
   * @returns Result that can be either an success (`ok` with `null` value) or an `KtznEventSubscriptionError`
   */
  unsubscribeFromEvent = async (subscribeName: string): Promise<Result<null, KtznEventSubscriptionError>> => {
    if (!subscribeName || subscribeName == "")
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.noSubscribeName));

    // If subscription exists for staff_group do not throw error, skip below if block
    if (!this._subscriptionCache.existByName(subscribeName) && !subscribeName.includes("staff_group/"))
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.subscriptionExists));

    const result = await this._authenticationRepository.getTokenString();
    if (result && result.isOk()) {
      const event = this._ktznWebsocketService.send(
        SendEventType.Unsubscribe,
        subscribeName,
        result.value,
        "ktzn_auth"
      );
      if (event && event.isOk()) {
        const res = this._subscriptionCache.removeByEvent(event.value);
        if (res.isOk()) return ok(res.value);
        else {
          return err(
            new KtznEventSubscriptionError(
              KtznEventSubscriptionError.Type.cacheSubscriptionDontExists,
              event && event.isErr() ? event.error.message : ""
            )
          );
        }
      } else {
        return err(
          new KtznEventSubscriptionError(
            KtznEventSubscriptionError.Type.sendFailed,
            event && event.isErr() ? event.error.message : ""
          )
        );
      }
    } else {
      const res = await this._errorLogService.sendAuthLog(
        "Auth.currentAuthenticatedUser",
        "getNewToken",
        {},
        result.error.message
      );
      if (res.isErr()) {
        console.log("sendAuthLog failed: ", res.error.message);
      }
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.getCogntioTokenFailed));
    }
  };

  /**
   * Unsubscribes from all events.
   *
   * ⚠️ Needs to be run at logout.
   * @returns Result that can be either an success (`ok` with array `null` values) or an `KtznEventSubscriptionError`
   */
  unsubscribeFromAllEvents = async (): Promise<Result<null[], KtznEventSubscriptionError>> => {
    // ログアウト時に実行する
    let promises: Promise<Result<null, KtznEventSubscriptionError>>[] = [];
    let list = this._subscriptionCache.list();
    list.forEach((item: KtznEventSubscription) => {
      if (item && item.event && item.event.subscribe_name) {
        promises.push(this.unsubscribeFromEvent(item.event.subscribe_name));
      } else {
        console.log("unsubscribeFromAllEvents error: No subscribe_name for item: ", item);
      }
    });
    try {
      const res = combine(await Promise.all(promises));
      if (res && res.isErr())
        return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.unsubscribeFromAllFailed));
      // Reset `userAccessId` and `tenantId`
      this.userAccessId = undefined;
      this.tenantId = undefined;
      // Return the combine result
      return res;
    } catch (error) {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.unsubscribeFromAllFailed, error));
    }
  };

  /**
   * Helper to subscribe to a user.
   * @param accessId User Identifier (10 digit string)
   * @returns Result that can be either an success (`ok` with `KtznEventSubscription` value) or an `KtznEventSubscriptionError`
   */
  subscribeToUser = async (
    accessId: string | undefined
  ): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>> => {
    if (!accessId) {
      return err(
        new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.subscribeToUserFailed, "userId missing")
      );
    }
    return await this.subscribeToEvent(`user/${accessId}`);
  };

  /**
   * Helper to subscribe to a tenant.
   *
   * Calls `subscribeToEvent` with the `tenant` prefix internally.
   * @param tenantId Tenant Identifier (Numerical)
   * @returns Result that can be either an success (`ok` with `KtznEventSubscription` value) or an `KtznEventSubscriptionError`
   */
  subscribeToTenant = async (
    tenantId: number | undefined
  ): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>> => {
    if (tenantId == null || tenantId == undefined) {
      return err(
        new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.subscribeToTenantFailed, "tenantId missing")
      );
    }
    const sTenantId = tenantId.toString();
    // Copied this logic from previous code to not break behavior. Why it is neded?
    if (!this.tenantId) {
      this.tenantId = sTenantId;
    } else if (this.tenantId !== sTenantId) {
      await this.unsubscribeFromEvent(`tenant/${this.tenantId}`);
      this.tenantId = sTenantId;
    }
    return await this.subscribeToEvent(`tenant/${this.tenantId}`);
  };

  /**
   * Convenience helper to subscribe to a conversation.
   *
   * Calls `subscribeToEvent` with the `conversation` prefix internally.
   * @param conversationId Conversation Identifier (UUID string)
   * @returns Result that can be either an success (`ok` with `KtznEventSubscription` value) or an `KtznEventSubscriptionError`
   */
  subscribeToConversation = async (
    conversationId: string | undefined
  ): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>> => {
    if (!conversationId) {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.subscribeToConversationFailed,
          "conversationId missing"
        )
      );
    }
    return await this.subscribeToEvent(`conversation/${conversationId}`);
  };

  /**
   * Subscribe to staff groups
   *
   * @param staffGroupId login staff group id
   * @returns Result that can be either an success (`ok` with list `KtznEventSubscription` values) or an `KtznEventSubscriptionError`
   */

  subscribeToStaffGroup = async (
    staffGroupId: number
  ): Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>> => {
    if (!staffGroupId) {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.subscribeToConversationFailed,
          "staffGroupId missing"
        )
      );
    }
    return await this.subscribeToEvent(`staff_group/${staffGroupId}`);
  };

  /**
   * Helper to subscribe to a a list of conversation ID's.
   *
   * @param conversationIds List of Conversation Identifiers (UUID string)
   * @returns Result that can be either an success (`ok` with list `KtznEventSubscription` values) or an `KtznEventSubscriptionError`
   */
  subscribeToConversations = async (
    conversationIds: string[] | undefined
  ): Promise<Result<KtznEventSubscription[], KtznEventSubscriptionError>> => {
    if (!conversationIds || conversationIds.length == 0) {
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.subscribeToConversationsFailed,
          "conversationIds missing"
        )
      );
    }
    let promises: Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>>[] = [];
    conversationIds.forEach((id) => promises.push(this.subscribeToConversation(id)));
    try {
      // Return the combine result
      return combine(await Promise.all(promises));
    } catch (error) {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.general, error));
    }
  };

  /**
   * Unsubscribes from the conversation events but subscribes to the user and tenant.
   *
   * Transfered from previous code. Is this still in use???
   * Previous name: `unsubscribe_conversation_events`
   * @returns Result that can be either an success (`ok` with `null` value) or an `KtznEventSubscriptionError`
   */
  unsubscribeConversationEvents = async (): Promise<Result<null, KtznEventSubscriptionError>> => {
    let promises: Promise<Result<KtznEventSubscription | null, KtznEventSubscriptionError>>[] = [];
    let list = this._subscriptionCache.list();
    list.forEach((item: KtznEventSubscription) => {
      if (item && item.event && item.event.subscribe_name && item.event.subscribe_name.indexOf("conversation") >= 0) {
        promises.push(this.unsubscribeFromEvent(item.event.subscribe_name));
      } else {
        console.log("unsubscribeConversationEvents error: No subscribe_name for item: ", item);
      }
    });
    try {
      // Return the combine result
      combine(await Promise.all(promises));
    } catch (error) {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.general, error));
    }

    if (this.userAccessId == null || this.userAccessId == undefined) {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.general, "No userAccessId"));
    }
    // userサブスクライブはチャット以外で使用される為、常にsubscribeさせる
    // this.add_subscribe_event(`user/${this.userAccessId}`);
    const userRes = await this.subscribeToUser(this.userAccessId);
    if (userRes.isErr() && userRes.error.type != KtznEventSubscriptionError.Type.subscriptionExists)
      return err(userRes.error);

    if (this.tenantId == null || this.tenantId == undefined) {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.general, "No tenantId"));
    }
    // tenantサブスクライブはチャット以外で使用される為、常にsubscribeさせる
    // this.add_subscribe_event(`tenant/${this.tenantId}`);
    const tenantRes = await this.subscribeToTenant(~~this.tenantId);
    if (tenantRes.isErr() && tenantRes.error.type != KtznEventSubscriptionError.Type.subscriptionExists)
      return err(tenantRes.error);

    return ok(null);
  };

  /**
   * Resubscribes to all not connected subscriptions.
   * @returns Result that either contains a list of successfully re-subscribed KtznEventSubscriptions or an KtznEventSubscriptionError.
   */
  reSubscribe = async (): Promise<Result<KtznEventSubscription[], KtznEventSubscriptionError>> => {
    let promises: Promise<Result<KtznEventSubscription, KtznEventSubscriptionError>>[] = [];
    let list = this._subscriptionCache.list();
    if (list == null) return ok([]);
    console.log("reSubscribe to: ", list);
    list.forEach((item: KtznEventSubscription) => {
      if (item && item.event && item.event.subscribe_name) {
        promises.push(this.subscribeToEvent(item.event.subscribe_name));
      } else {
        console.log("reSubscribe error: No subscribe_name for item: ", item);
      }
    });
    try {
      const res = await Promise.all(promises);
      // Return the combine result
      return combine(res);
    } catch (error) {
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.reSubscribeFailed, error));
    }
  };

  // MARK: - Lifecycle

  /**
   * Starts the Kotozna subscriptions
   * @param userAccessId User ID (from vuex store or Cogntio token)
   * @param webSocketUri WebSocket URI (from vuex store - included in the login result)
   * @returns Result that can be either an success (`ok` with `null` value) or an `KtznEventSubscriptionError`
   */
  start = async (userAccessId: string, webSocketUri: string): Promise<Result<null, KtznEventSubscriptionError>> => {
    console.log("KtznEventSubscriptionRepository - start()");
    if (this._connectionStarted) {
      console.log("start - already connected");
      return err(new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.alreadyConnected));
    }

    this._connectionStarted = true;

    this.userAccessId = userAccessId;
    this._connectionStarted = this._ktznWebsocketService.connect(webSocketUri);
    // reSubscribe
    const subscribeToUserResult = await this.subscribeToEvent(`user/${userAccessId}`);
    if (subscribeToUserResult.isErr()) {
      return err(
        new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.subscribeFailed, subscribeToUserResult.error)
      );
    }
    const reSubscribeResult = await this.reSubscribe();
    if (reSubscribeResult.isErr()) {
      return err(
        new KtznEventSubscriptionError(KtznEventSubscriptionError.Type.reSubscribeFailed, reSubscribeResult.error)
      );
    }
    return ok(null);
  };

  /**
   * Stops the current websocket connection.
   * @returns Result that can be either an success (`ok` with `null` value) or an `KtznEventSubscriptionError`
   */
  stop = async (): Promise<Result<null, KtznEventSubscriptionError>> => {
    // unsubscribe from all subscriptions
    const unsubscribeFromAllEventsResult = await this.unsubscribeFromAllEvents();
    if (unsubscribeFromAllEventsResult.isErr()) {
      console.log("stop - unsubscribeFromAllEvents failed with error: " + unsubscribeFromAllEventsResult.error);
      return err(
        new KtznEventSubscriptionError(
          KtznEventSubscriptionError.Type.unSubscribeFailed,
          unsubscribeFromAllEventsResult.error
        )
      );
    }
    // Clear cache
    this._subscriptionCache.removeAll();
    // Reset values
    this.userAccessId = undefined;
    this.tenantId = undefined;
    this._connectionStarted = false;
    return ok(null);
  };
}

export default new KtznEventSubscriptionRepository();
